import { useEffect, useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Users, Mail, Phone, Download, Search } from 'lucide-react';

import { queryKeys } from '@/lib/api/queryKeys';
import { contactsApi } from './api';
import { AcknowledgmentDialog } from '@/features/legal/AcknowledgmentDialog';
import { brandLegalApi } from '@/features/brands/api';
import { useAuth } from '@/lib/auth/useAuth';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import type { CoContactFilters } from '@/lib/api/types';

/**
 * Acotación impuesta desde fuera: la que aplica Insights con sus filtros de
 * marca, proyecto, evento y fechas.
 *
 * Va aparte de los filtros propios de la página —búsqueda, consentimiento,
 * paginación— porque no son lo mismo: éstos los pone quien mira la tabla y
 * aquéllos vienen de la pantalla que la embebe. Mezclarlos en un solo estado
 * hacía que un `setFilters` de la búsqueda borrase el alcance sin querer.
 */
export type ContactsScope = Pick<
  CoContactFilters,
  'brandId' | 'eventGroupId' | 'eventId' | 'eventIds' | 'from' | 'to'
>;

/**
 * Caso 01 — Leads CRM. Replaces the legacy mock-grouped Responses view
 * with a real CRM-grade contact table sourced from /co-contacts. The page
 * unit IS the person (deduped by email or phone), not the response row.
 *
 * Embebida en Insights recibe `scope`: la misma acotación que ven las demás
 * pestañas. Sin él —la ruta `/contacts`— enseña todo el fondo de leads del
 * espacio de trabajo, que es lo que esa pantalla debe hacer.
 */
export function ContactsPage({ scope }: { scope?: ContactsScope } = {}) {
  const t = useT();
  useFavoritable(t('contacts.title'));
  const navigate = useNavigate();

  const [filters, setFilters] = useState<CoContactFilters>({
    page: 1,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');

  /**
   * Lo que de verdad se pide al servidor. El alcance manda sobre los filtros
   * propios: si Insights dice «este proyecto», la tabla no puede enseñar otro.
   */
  const effective = useMemo<CoContactFilters>(
    () => ({ ...filters, ...clean(scope) }),
    [filters, scope],
  );

  // Cambiar de proyecto o de evento deja la página 7 sin existir. Se vuelve a
  // la primera, o la tabla sale vacía y parece que el filtro no encontró nada.
  const scopeKey = JSON.stringify(clean(scope));
  useEffect(() => {
    setFilters((f) => (f.page === 1 ? f : { ...f, page: 1 }));
  }, [scopeKey]);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.contacts.list(
      effective as unknown as Record<string, unknown>,
    ),
    queryFn: () => contactsApi.list(effective),
    placeholderData: keepPreviousData,
  });

  function applySearch() {
    setFilters((f) => ({ ...f, q: searchInput || undefined, page: 1 }));
  }

  function toggleConsent() {
    setFilters((f) => ({
      ...f,
      consent: f.consent ? undefined : true,
      page: 1,
    }));
  }

  function setPage(page: number) {
    setFilters((f) => ({ ...f, page }));
  }

  /*
   * Exportar pasa por el Data Export Acknowledgment (Exhibit F, p. 2): la marca
   * toma la custodia de la copia y un representante lo firma ANTES. El backend
   * exige la firma (428 sin ella), así que el botón abre el diálogo y la
   * descarga se lanza con el id de la fila firmada.
   *
   * El firmante es el alcance de lo que se exporta: la marca cuando la pantalla
   * que nos embebe acota por marca (Insights › Leads), y la cuenta en
   * /contacts, cuyo CSV reúne todas las marcas de la cuenta. Firmar «on behalf
   * of» la marca del selector ahí afirmaría algo falso con más de una marca.
   *
   * `requestedAt` existe porque el servidor es idempotente por el hash del
   * payload: sin algo que cambie, dos exportaciones iguales serían una sola fila
   * y repetirla pasados 15 min daría 428 (la fila vieja ya no vale).
   */
  const { companyId: miEmpresa } = useAuth();
  const firmante: number | null = effective.brandId ?? miEmpresa ?? null;
  const legalQ = useQuery({
    queryKey: queryKeys.brands.legal(firmante ?? 0),
    queryFn: () => brandLegalApi.get(firmante!),
    enabled: !!firmante,
    staleTime: 300_000,
  });
  const [firmandoExport, setFirmandoExport] = useState(false);
  const [exportPedidoEn, setExportPedidoEn] = useState<string | null>(null);

  async function downloadCsv(exportAckId: number) {
    // Con `effective`, no con `filters`: el CSV es lo que se está viendo.
    const blob = await contactsApi.exportCsv(effective, exportAckId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${dayjs().format('YYYYMMDD-HHmm')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = effective.page ?? 1;
  const limit = effective.limit ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="page space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            {t('contacts.title')}
            <HelpHint section="contacts-list" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
            {t('contacts.subtitle')}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            setExportPedidoEn(new Date().toISOString());
            setFirmandoExport(true);
          }}
          disabled={total === 0 || !firmante || legalQ.isLoading}
        >
          <Download className="mr-2 h-4 w-4" />
          {t('contacts.export')}
        </Button>
      </header>

      {firmante ? (
        <AcknowledgmentDialog
          key={firmante}
          open={firmandoExport}
          onOpenChange={setFirmandoExport}
          documentType="data_export_ack"
          brandId={firmante}
          brandLegalName={legalQ.data?.legalName || legalQ.data?.brandName || ''}
          subject={t('contacts.exportAckSubject', { n: String(total) })}
          summary={[
            { label: t('contacts.title'), value: String(total) },
            { label: 'CSV', value: t('contacts.exportFields') },
          ]}
          payload={{
            dataset: 'contacts',
            rows: total,
            format: 'csv',
            filters: effective as unknown as Record<string, unknown>,
            fields: 'contact + context + one column per question',
            requestedAt: exportPedidoEn,
          }}
          onAcknowledged={(ack) => {
            setFirmandoExport(false);
            void downloadCsv(ack.id);
          }}
        />
      ) : null}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 pt-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-fg-subtle)]" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applySearch()}
              placeholder={t('contacts.searchPlaceholder')}
              className="w-full rounded-md border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-9 py-2 text-sm"
            />
          </div>
          <Button variant="secondary" onClick={applySearch}>
            {t('contacts.applySearch')}
          </Button>
          <Button
            variant={filters.consent ? 'primary' : 'secondary'}
            onClick={toggleConsent}
          >
            {t('contacts.filter.consentOnly')}
          </Button>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-40" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" />}
          title={t('contacts.empty.title')}
          description={t('contacts.empty.body')}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>
              {t('contacts.totalCount', { count: total })}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-[var(--color-fg-subtle)]">
                <tr>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.name')}
                  </th>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.contact')}
                  </th>
                  <th className="py-2 text-right font-semibold tabular-nums">
                    {t('contacts.col.interactions')}
                  </th>
                  <th className="py-2 text-right font-semibold tabular-nums">
                    {t('contacts.col.prizes')}
                  </th>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.lastSeen')}
                  </th>
                  <th className="py-2 text-left font-semibold">
                    {t('contacts.col.consent')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const name =
                    [c.firstName, c.lastName].filter(Boolean).join(' ').trim() ||
                    t('contacts.anonymousName');
                  return (
                    <tr
                      key={c.id}
                      className="cursor-pointer border-t border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)]"
                      onClick={() => navigate(`/contacts/${c.id}`)}
                    >
                      <td className="py-3 font-medium">{name}</td>
                      <td className="py-3 text-[var(--color-fg-muted)]">
                        <ContactSnippet
                          email={c.email}
                          phone={c.phone}
                          placeholder={t('contacts.anonymousSnippet')}
                        />
                      </td>
                      <td className="py-3 text-right tabular-nums">
                        {c.interactionsCount}
                      </td>
                      <td className="py-3 text-right tabular-nums">
                        {c.prizesWonCount}
                      </td>
                      <td className="py-3">
                        {c.lastSeenAt
                          ? dayjs(c.lastSeenAt).format('MMM D, YYYY')
                          : '—'}
                      </td>
                      <td className="py-3">
                        <span className="inline-flex flex-wrap gap-1">
                          {c.marketingConsent ? (
                            <Badge tone="success">
                              {t('contacts.consent.yes')}
                            </Badge>
                          ) : (
                            <Badge tone="neutral">
                              {t('contacts.consent.no')}
                            </Badge>
                          )}
                          {/* SMS es OTRO consentimiento: se enseña aparte y sólo si lo dio. */}
                          {c.smsConsent ? (
                            <Badge tone="info">{t('contacts.consent.sms')}</Badge>
                          ) : null}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination
              page={page}
              pageCount={totalPages}
              total={total}
              from={total === 0 ? 0 : (page - 1) * limit + 1}
              to={Math.min(page * limit, total)}
              setPage={setPage}
              next={() => setPage(Math.min(page + 1, totalPages))}
              prev={() => setPage(Math.max(page - 1, 1))}
              noun={t('contacts.noun')}
            />

          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Quita las claves sin valor: `eventId: undefined` no debe pisar nada. */
function clean(scope?: ContactsScope): ContactsScope {
  if (!scope) return {};
  return Object.fromEntries(
    Object.entries(scope).filter(
      ([, v]) => v !== undefined && v !== '' && v !== null && !(Array.isArray(v) && v.length === 0),
    ),
  ) as ContactsScope;
}

function ContactSnippet(props: {
  email: string | null;
  phone: string | null;
  placeholder: string;
}) {
  if (!props.email && !props.phone) {
    return (
      <span className="text-[var(--color-fg-subtle)]">{props.placeholder}</span>
    );
  }
  return (
    <span className="inline-flex flex-col gap-0.5">
      {props.email && (
        <span className="inline-flex items-center gap-1">
          <Mail className="h-3 w-3" />
          {props.email}
        </span>
      )}
      {props.phone && (
        <span className="inline-flex items-center gap-1">
          <Phone className="h-3 w-3" />
          {props.phone}
        </span>
      )}
    </span>
  );
}
